import React, { memo } from 'react';
import { Checkbox, Icons } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { Pressable, View } from 'react-native';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';

// Defined outside createStyles because the drag handle is a raw <div> (required by
// @hello-pangea/dnd's DOM-native dragHandleProps) and needs plain CSS, not RN ViewStyle.
const dragHandleStyle: React.CSSProperties = {
  cursor: 'grab',
  display: 'flex',
};

interface ConfigureColumnRowProps {
  columnId: string;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  isDragging: boolean;
  isVisible: boolean;
  label: string;
  onToggle: (columnId: string) => void;
}

/**
 * A single row in the configure-columns popover. Clicking the row toggles
 * column visibility; dragging via the grip handle reorders the column.
 */
export const ConfigureColumnRow: React.FC<ConfigureColumnRowProps> = memo(
  ({ columnId, dragHandleProps, isDragging, isVisible, label, onToggle }) => {
    const [styles, theme] = useStyles();

    return (
      <Pressable
        onPress={() => onToggle(columnId)}
        style={({ hovered }) => [
          styles.row,
          hovered && styles.rowHovered,
          isDragging && styles.rowDragging,
        ]}
      >
        <View style={styles.rowLeft}>
          <Checkbox
            checked={isVisible}
            pointerEvents="none"
            size="$sm"
            tabIndex={-1}
          />
          <View style={styles.label}>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {label}
            </Typography>
          </View>
        </View>
        <div {...dragHandleProps} style={dragHandleStyle}>
          <Icons.GripVertical
            color={theme.sk.color.icon.tertiary}
            size={theme.sk.spacing.spaceMedium}
          />
        </div>
      </Pressable>
    );
  },
);

const useStyles = createStyles((theme) => ({
  label: {
    flexShrink: 1,
  },
  row: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  rowDragging: {
    backgroundColor: theme.sk.color.bg.primary.default,
    ...shadow({
      elevation: 1,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  rowHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  rowLeft: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    minWidth: 0,
  },
}));
